import React, { useState, useCallback, useRef, useEffect } from 'react';
import useDimensions from 'react-cool-dimensions'
import './Table.scss';

interface Column {
    id: string;
    label: string;
}

interface TableProps {
    columns: Column[];
    data: any[];
}
function renderData(obj:any) {
    if (typeof obj == 'object') {
        return <div className='json-data'>{JSON.stringify(obj)}</div>;
    }
    if (typeof obj == 'boolean') {
        return <div className='boolean-data'>{obj?'true':'false'}</div>;
    }
    if (typeof obj == 'number') {
        return <div className='numeric-data'>{''+obj}</div>;
    }
    return obj;
}
const minCellWidth = 200
const Table: React.FC<TableProps> = ({ columns, data }) => {

    const [columnWidths, setColumnWidths] = useState<{ [key: string]: { width: number, minWidth: number } }>(null);

    const tableContainerRef = useRef(null)

    useEffect(() => {
        if (tableContainerRef?.current) {
            const { width } = (tableContainerRef.current as HTMLDivElement).getBoundingClientRect()
            let minWidth = columns?.length > 0 ? ((width - 10) / columns.length) : (width - 10)
            if (minWidth < minCellWidth) minWidth = minCellWidth
            setColumnWidths(columns.reduce((acc, column) => ({ ...acc, [column.id]: { width: minWidth, minWidth: minWidth } }), {}))
        }

    }, [tableContainerRef.current, columns])

    const handleResize = useCallback((columnId: string, newWidth: number) => {
        setColumnWidths(prevWidths => ({
            ...prevWidths,
            [columnId]: { ...prevWidths[columnId], width: newWidth },
        }));
    }, []);

    const getTotalWidth = () => {
        if (!columnWidths) return '100%';
        return Object.values(columnWidths).reduce((total, { width }) => total + width, 0);
    };

    return (
        <div className='resizable-table-container' ref={tableContainerRef} >
            {
                !columnWidths
                    ? <>Loading...</>
                    : <div className="resizable-table" style={{ minWidth: getTotalWidth() }} >
                        <div className="resizable-table__header">
                            {columns.map(column => (
                                <div
                                    key={column.id}
                                    className="resizable-table__header-cell"
                                    style={{ ...columnWidths[column.id] }}
                                >
                                    {column.label}
                                    <div
                                        className="resizable-table__resize-handle"
                                        onMouseDown={e => {
                                            const startX = e.clientX;
                                            const startWidth = (e.target as HTMLElement).parentElement?.offsetWidth;

                                            const onMouseMove = (e: MouseEvent) => {
                                                const diff = e.clientX - startX;
                                                handleResize(column.id, Math.max(startWidth + diff, minCellWidth));
                                            };

                                            const onMouseUp = () => {
                                                document.removeEventListener('mousemove', onMouseMove);
                                                document.removeEventListener('mouseup', onMouseUp);
                                            };

                                            document.addEventListener('mousemove', onMouseMove);
                                            document.addEventListener('mouseup', onMouseUp);
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="resizable-table__body">
                            {data.map((row, rowIndex) => (
                                <div key={rowIndex} className="resizable-table__row">
                                    {columns.map(column => (
                                        <div
                                            key={column.id}
                                            className="resizable-table__cell"
                                            style={{ ...columnWidths[column.id] }}
                                        >
                                            {renderData(row[column.id])}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
            }
        </div>
    );
};

export default Table;